import type { FC, ReactNode } from "react";

import { Alert } from "./Alert";
import { EmptyState } from "./EmptyState";
import { LoadingState } from "./LoadingState";

export interface DataStateProps {
  /**
   * Whether the data is loading
   */
  loading?: boolean;

  /**
   * Error message if an error occurred
   */
  error?: string | null;

  /**
   * Whether the data is empty
   */
  empty?: boolean;

  /**
   * Content to render when data is available
   */
  children: ReactNode;

  /**
   * Optional message to display while loading
   */
  loadingMessage?: string;

  /**
   * Title for the empty state
   */
  emptyTitle?: string;

  /**
   * Description for the empty state
   */
  emptyDescription?: string;

  /**
   * Optional action for the empty state
   */
  emptyAction?: ReactNode;

  /**
   * Custom component to render for loading state
   */
  loadingComponent?: ReactNode;

  /**
   * Custom component to render for empty state
   */
  emptyComponent?: ReactNode;

  /**
   * Custom component to render for error state
   */
  errorComponent?: ReactNode;

  /**
   * Optional action to display when in error state
   */
  errorAction?: ReactNode;

  /**
   * Additional CSS classes for the container
   */
  className?: string;
}

/**
 * DataState Component
 *
 * A wrapper component that handles loading, error, and empty states for data-driven UI.
 *
 * @example
 * ```tsx
 * <DataState
 *   loading={isLoading}
 *   error={error}
 *   empty={items.length === 0}
 *   emptyTitle="No items found"
 * >
 *   <List items={items} />
 * </DataState>
 * ```
 */
export const DataState: FC<DataStateProps> = ({
  loading = false,
  error = null,
  empty = false,
  children,
  loadingMessage,
  emptyTitle = "No data available",
  emptyDescription,
  emptyAction,
  loadingComponent,
  emptyComponent,
  errorComponent,
  errorAction,
  className = "",
}) => {
  if (error) {
    return (
      <div className={className}>
        {errorComponent ?? (
          <div className="flex flex-col gap-3">
            <Alert variant="warning" size="sm">
              {error}
            </Alert>
            {errorAction && <div>{errorAction}</div>}
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className={className}>
        {loadingComponent ?? (
          <LoadingState
            variant="skeleton"
            message={loadingMessage}
            rowHeight="h-9"
          />
        )}
      </div>
    );
  }

  if (empty) {
    return (
      <div className={className}>
        {emptyComponent ?? (
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
            action={emptyAction}
            size="sm"
          />
        )}
      </div>
    );
  }

  return <div className={className}>{children}</div>;
};
